import '../wwwroot/js/profint-camera-zoom.js';
import '../wwwroot/js/profint-portrait-editor.js';
// Shared host bridge. Algorithms, OCR and models remain in the versioned Gateway package.
type CaptureProfile = 'dni-front' | 'dni-back' | 'license-front' | 'license-back' | 'portrait' | 'document';
export function installCaptureQuality(engines: {
    captureFrameWithQuality: (...args: any[]) => Promise<any>;
    prepareIdentityImage: (...args: any[]) => Promise<any>;
    frameIdentityDocument?: (...args: any[]) => Promise<any>;
    assessIdentityImage: (...args: any[]) => Promise<any>;
    detectCardEdges: (...args: any[]) => any;
    captureGuide: (profile: CaptureProfile) => any;
    startFaceGuide: (...args: any[]) => { stop(): void };
    createFaceWorker: () => Worker;
    startLiveDocumentGuide: (...args: any[]) => { stop(): void };
    createDocumentWorker: () => Worker;
    createDocumentFramingWorker?: () => Worker;
    loadDocumentFraming?: () => Promise<any>;
    createPortraitWorker: () => Worker;
    loadPortraitBackground: () => Promise<any>;
    loadDniOcr: () => Promise<any>;
}) {
    if ((window as any).ProfintCameraQuality) return (window as any).ProfintCameraQuality;
    const { captureFrameWithQuality, prepareIdentityImage, assessIdentityImage, captureGuide, startFaceGuide, createFaceWorker, startLiveDocumentGuide, createDocumentWorker, createDocumentFramingWorker, loadDocumentFraming, createPortraitWorker, loadPortraitBackground, loadDniOcr } = engines;
function profileFor(kind: string): CaptureProfile {
    if (['dni-front', 'dni-back', 'license-front', 'license-back', 'portrait', 'document'].includes(kind)) return kind as CaptureProfile;
    return ({ rostro: 'portrait', 'dni-frente': 'dni-front', 'dni-dorso': 'dni-back',
        'licencia-frente': 'license-front', 'licencia-dorso': 'license-back' } as Record<string, CaptureProfile>)[kind] ?? 'document';
}

function guideFor(kind: string) { return captureGuide(profileFor(kind)); }

const enhancementTasks = new Set<AbortController>();
window.addEventListener('pagehide', () => { enhancementTasks.forEach(task => task.abort()); enhancementTasks.clear(); });
function boundedEnhancement<T>(work: (signal: AbortSignal) => Promise<T>, signal?: AbortSignal, timeoutMs = 8000): Promise<T> {
    const controller = new AbortController(); enhancementTasks.add(controller);
    return new Promise<T>((resolve, reject) => {
        let finished = false;
        const finish = (value?: T, error?: unknown) => {
            if (finished) return; finished = true;
            clearTimeout(timer); signal?.removeEventListener('abort', cancel);
            controller.signal.removeEventListener('abort', aborted); enhancementTasks.delete(controller);
            if (error !== undefined) reject(error); else resolve(value as T);
        };
        const cancel = () => controller.abort(signal?.reason);
        const aborted = () => finish(undefined, controller.signal.reason);
        const timer = setTimeout(() => controller.abort(new DOMException('Optional photo enhancement timed out.', 'TimeoutError')), timeoutMs);
        controller.signal.addEventListener('abort', aborted, { once: true });
        signal?.addEventListener('abort', cancel, { once: true });
        if (signal?.aborted) { cancel(); return; }
        Promise.resolve().then(() => { controller.signal.throwIfAborted(); return work(controller.signal); })
            .then(value => finish(value), error => finish(undefined, error));
    });
}

function faceFrame(face: { x: number; y: number; width: number; height: number; eyes: { x: number; y: number }[] }, width: number, height: number) {
    const [leftEye, rightEye] = face.eyes;
    if (!leftEye || !rightEye || width <= 0 || height <= 0) return face;
    const eyeDistance = Math.hypot((rightEye.x - leftEye.x) * width, (rightEye.y - leftEye.y) * height);
    if (!Number.isFinite(eyeDistance) || eyeDistance < Math.min(width, height) * .035) return face;

    // BlazeFace's detection rectangle can start around the nose on low light or
    // partial camera frames. The eyes are much more stable anchors: extend the
    // visible guide up to the forehead and down to the chin from their spacing.
    const centerX = (leftEye.x + rightEye.x) / 2;
    const eyeY = (leftEye.y + rightEye.y) / 2;
    const guideWidth = Math.max(face.width, eyeDistance / width * 2.7);
    const guideHeight = Math.max(face.height, eyeDistance / height * 3.4);
    const x = Math.max(0, Math.min(1 - guideWidth, centerX - guideWidth / 2));
    const y = Math.max(0, Math.min(1 - guideHeight, eyeY - eyeDistance / height * 2));
    return { x, y, width: Math.min(guideWidth, 1 - x), height: Math.min(guideHeight, 1 - y), eyes: face.eyes };
}

/** Reuse Aether's transient camera primitive; no identity/biometric transport is invoked. */
async function capture(video: HTMLVideoElement) {
    await (window as any).ProfintCameraZoom?.whenReady(video);
    const edge = Math.max(video.videoWidth, video.videoHeight);
    if (!edge || !video.videoWidth || !video.videoHeight) throw new Error('Esperá a que la cámara esté lista.');
    const width = Math.max(1, Math.round(video.videoWidth * Math.min(1, 2400 / edge)));
    const frame = await captureFrameWithQuality(video, width);
    return frame;
}

/** Soft size budget: preserve document resolution/legibility instead of chasing a smaller file. */
async function optimizeForUpload(file: File, kind: string, crop?: { x: number; y: number; width: number; height: number }, options: { signal?: AbortSignal } = {}) {
    if (!file.type.startsWith('image/')) return file;
    try {
        return await boundedEnhancement(async signal => {
            const result = await prepareIdentityImage(file, { profile: profileFor(kind), crop, signal });
            if (!(result?.blob instanceof Blob) || result.blob.type !== 'image/jpeg' || !result.blob.size || result.blob.size > 8 * 1024 * 1024) return file;
            const optimized = new File([result.blob], file.name.replace(/\.[^.]+$/, '') + '-optimizada.jpg', { type: 'image/jpeg' });
            return crop || optimized.size < file.size ? optimized : file;
        }, options.signal);
    } catch (error) { if (options.signal?.aborted) throw error; return file; }
}

async function assessImage(file: File, kind: string, options: { documentFramed?: boolean; signal?: AbortSignal } = {}) {
    try { return await boundedEnhancement(signal => assessIdentityImage(file, { ...options, signal, profile: profileFor(kind) }), options.signal); }
    catch (error) { if (options.signal?.aborted) throw error; return null; }
}

async function clearPortraitBackground(file: File, signal?: AbortSignal) {
    try { return (await portraitBackground(file, 'white', signal))?.file ?? null; }
    catch (error) { if (signal?.aborted) throw error; return null; }
}
async function editPortraitBackground(file: File, mode: 'white' | 'blur', signal?: AbortSignal) {
    if (!['white', 'blur'].includes(mode)) throw new Error('Elegí un fondo blanco o difuminado.');
    // An explicit edit may wait for the cold WASM download while the original
    // remains usable. Automatic enhancements retain their shorter deadline.
    return portraitBackground(file, mode, signal, 30000);
}
async function portraitBackground(file: File, mode: 'light' | 'white' | 'blur', signal?: AbortSignal, timeoutMs = 8000) {
    return boundedEnhancement(async activeSignal => {
        const { replacePortraitBackground } = await loadPortraitBackground();
        activeSignal.throwIfAborted();
        const result = await replacePortraitBackground(file, {
            createWorker: () => createPortraitWorker(), wasmPath: '/dist/face/v1/wasm',
            modelPath: '/dist/face/v1/selfie-segmenter-square-float16-v1.tflite',
            faceModelPath: '/dist/face/v1/blaze-face-short-range-v1.tflite',
            signal: activeSignal, mode,
        });
        return result ? { file: new File([result.blob], file.name.replace(/\.[^.]+$/, '') + '-fondo-' + (mode === 'blur' ? 'difuminado' : mode === 'white' ? 'blanco' : 'claro') + '.jpg', { type: 'image/jpeg' }),
            mode: result.mode || mode, foregroundBounds: result.foregroundBounds } : null;
    }, signal, timeoutMs);
}

async function prepareForReview(file: File, kind: string, action: 'detect' | 'rotate' | 'flip', signal?: AbortSignal) {
    const profile = profileFor(kind);
    try {
        return await boundedEnhancement(async activeSignal => {
            let result;
            if (action === 'detect') {
                if (!loadDocumentFraming || !createDocumentFramingWorker) return null;
                const { frameIdentityDocumentInWorker } = await loadDocumentFraming();
                activeSignal.throwIfAborted();
                result = await frameIdentityDocumentInWorker(file, { profile, signal: activeSignal, createWorker: createDocumentFramingWorker });
            } else result = await prepareIdentityImage(file, { profile, rotation: action === 'flip' ? 180 : 90, signal: activeSignal });
            return result?.blob instanceof Blob && result.blob.type === 'image/jpeg' && result.blob.size > 0 && result.blob.size <= 8 * 1024 * 1024
                ? new File([result.blob], file.name.replace(/\.[^.]+$/, '') + '-encuadre.jpg', { type: 'image/jpeg' }) : null;
        }, signal);
    } catch (error) { if (signal?.aborted) throw error; return null; }
}

async function readDni(file: File, signal?: AbortSignal, onProgress?: (progress: number) => void) {
    const { readArgentineDni } = await loadDniOcr();
    return readArgentineDni(file, {
        workerPath: '/dist/ocr/v1/worker.min.js', corePath: '/dist/ocr/v1/core', langPath: '/dist/ocr/v1/lang',
        signal: signal ? AbortSignal.any([signal, AbortSignal.timeout(45000)]) : AbortSignal.timeout(45000), onProgress,
    });
}

(window as any).ProfintCameraQuality = { capture, optimizeForUpload, prepareForReview, assessImage, clearPortraitBackground, editPortraitBackground, guideFor, readDni, stopGuides };

const guides = new WeakMap<Element, { stop: () => void }>();
const documentGuides = new WeakMap<Element, { stop: () => void }>();
function startDocumentGuide(form: HTMLElement, video: HTMLVideoElement, status?: HTMLElement | null) {
    documentGuides.get(form)?.stop();
    const viewport = video.parentElement;
    if (!viewport || !video.videoWidth || !video.videoHeight) return;
    const overlay = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    overlay.setAttribute('data-live-card-edges', ''); overlay.setAttribute('aria-hidden', 'true');
    overlay.setAttribute('viewBox', '0 0 1000 1000'); overlay.setAttribute('preserveAspectRatio', 'none');
    Object.assign(overlay.style, { position: 'absolute', inset: '0', width: '100%', height: '100%', display: 'none', zIndex: '4', pointerEvents: 'none' });
    const polygon = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
    for (const [key, value] of Object.entries({ fill: 'none', stroke: '#51e0a5', 'stroke-width': '3', 'stroke-linejoin': 'round', 'vector-effect': 'non-scaling-stroke' })) polygon.setAttribute(key, value);
    overlay.append(polygon); viewport.append(overlay);
    if (getComputedStyle(viewport).position === 'static') viewport.style.position = 'relative';
    let stopped = false;
    let guide: { stop(): void } | undefined;
    const stop = () => { stopped = true; guide?.stop(); overlay.remove(); delete form.dataset.profintDocumentDetection; };
    documentGuides.set(form, { stop });
    form.dataset.profintDocumentDetection = 'loading';
    if (status) status.textContent = 'Preparando detección de bordes…';
    const unavailable = () => {
        if (stopped) return;
        overlay.style.display = 'none'; form.dataset.profintDocumentDetection = 'unavailable';
        if (status) status.textContent = 'No pudimos iniciar la guía de bordes. Podés encuadrar el documento completo y tomar la foto.';
    };
    try {
        guide = startLiveDocumentGuide(video, {
            createWorker: () => createDocumentWorker(),
            onResult: (result: { corners: readonly { x: number; y: number }[] | null; confidence: number }) => {
                if (stopped) return;
                if (!form.isConnected) { stop(); return; }
                const corners = result.corners;
                const found = result.confidence >= .8 && corners?.length === 4 && corners.every(p => Number.isFinite(p.x) && Number.isFinite(p.y) && p.x >= 0 && p.x <= 1 && p.y >= 0 && p.y <= 1);
                if (found) {
                    polygon.setAttribute('points', corners!.map(p => `${p.x * 1000},${p.y * 1000}`).join(' '));
                    overlay.style.display = 'block'; form.dataset.profintDocumentDetection = 'detected';
                    if (status) status.textContent = 'Bordes detectados. Mantené el documento quieto y capturá.';
                } else {
                    overlay.style.display = 'none'; form.dataset.profintDocumentDetection = 'searching';
                    if (status) status.textContent = 'Buscando bordes… acercá el DNI o la licencia y evitá reflejos.';
                }
            },
            onError: unavailable,
        });
    } catch { unavailable(); }
}
function startGuides(form: HTMLElement) {
    if (!form?.hasAttribute('data-profint-camera-enhanced')) return;
    const video = form.querySelector<HTMLVideoElement>('[data-lic-camera-video]');
    const status = form.querySelector<HTMLElement>('[data-camera-quality-status]');
    if (video && /^(dni|licencia|license)-/.test(form.dataset.profintCropKind || '')) {
        startDocumentGuide(form, video, status);
        return;
    }
    if (form.dataset.profintCropKind !== 'rostro') return;
    guides.get(form)?.stop();
    const box = form.querySelector<HTMLElement>('[data-face-guide-box]');
    const eyes = form.querySelectorAll<HTMLElement>('[data-face-guide-eye]');
    if (!video || !box) return;
    const publicCapture = form.dataset.captureAudience === 'public';
    const unavailableHint = publicCapture ? 'Centrá tu rostro en el óvalo, con buena luz, y tomá la foto.'
        : 'Guía automática no disponible. Podés usar el marco y ajustar el recorte manualmente.';
    if (status) status.textContent = publicCapture ? 'Ubicá tu rostro en el óvalo.' : 'Preparando detección local de rostro y ojos…';
    const hide = () => { box.hidden = true; eyes.forEach(eye => eye.hidden = true); delete form.dataset.profintFaceCrop; delete form.dataset.profintFaceDetectedAt; };
    let stopped = false;
    try {
    const guide = startFaceGuide(video, {
        createWorker: () => createFaceWorker(), wasmPath: '/dist/face/v1/wasm', modelPath: '/dist/face/v1/blaze-face-short-range-v1.tflite',
        onResult: result => {
            if (stopped) return;
            hide();
            if (!result.face) {
                if (status) status.textContent = result.count > 1 ? 'Hay más de una cara. Dejá una sola persona en el encuadre.' : 'Acercate y mirá al frente para encontrar el rostro.';
                return;
            }
            const face = faceFrame(result.face, video.videoWidth, video.videoHeight);
            Object.assign(box.style, { left: `${face.x * 100}%`, top: `${face.y * 100}%`, width: `${face.width * 100}%`, height: `${face.height * 100}%` }); box.hidden = false;
            face.eyes.forEach((point, index) => { if (eyes[index]) { eyes[index].hidden = false; eyes[index].style.left = `${point.x * 100}%`; eyes[index].style.top = `${point.y * 100}%`; } });
            const w = video.videoWidth, h = video.videoHeight;
            const side = Math.min(w, h, Math.max(face.width * w * 1.7, face.height * h * 1.8));
            const cx = (face.x + face.width / 2) * w, cy = (face.y + face.height * .6) * h;
            form.dataset.profintFaceCrop = JSON.stringify({ x: Math.max(0, Math.min(w - side, cx - side / 2)) / w,
                y: Math.max(0, Math.min(h - side, cy - side / 2)) / h, width: side / w, height: side / h });
            form.dataset.profintFaceDetectedAt = String(performance.now());
            if (status) status.textContent = 'Rostro y ojos detectados. Mantené la mirada al frente y capturá.';
        },
        onError: () => { if (stopped) return; hide(); if (status) status.textContent = unavailableHint; },
    });
    guides.set(form, { stop: () => { stopped = true; guide.stop(); hide(); } });
    } catch {
        hide();
        if (status) status.textContent = unavailableHint;
    }
}
function stopGuides(form: HTMLElement) {
    const faceGuide = guides.get(form), documentGuide = documentGuides.get(form);
    guides.delete(form); documentGuides.delete(form);
    try { faceGuide?.stop(); } finally { documentGuide?.stop(); }
}
document.addEventListener('profint:capture:camera-ready', event => startGuides(event.target as HTMLElement));
document.addEventListener('profint:capture:zoom-changed', event => startGuides(event.target as HTMLElement));
document.addEventListener('profint:capture:zoom-changing', event => stopGuides(event.target as HTMLElement));
document.addEventListener('profint:capture:camera-closed', event => stopGuides(event.target as HTMLElement));

return (window as any).ProfintCameraQuality;
}
